import type { Automation, ScopeId } from '@capture/shared';
import { MOMENT, effectByKey } from '@capture/shared';
import { SectionCard } from './SectionCard';
import styles from './panel.module.css';

interface ScopeField {
  name: string;
  label: string;
}

interface Props {
  automation: Automation;
  fields: ScopeField[];
  onChange: (next: Automation) => void;
  /** When set (A3 panel editing a multi-form job), the owning form is shown as
   *  a read-only value — moving an existing automation across forms is out of
   *  scope. The stepper (create flow) leaves this unset and picks the form
   *  with its own editable control instead. */
  ownerFormLabel?: string;
}

const SCOPE_OPTIONS: { value: ScopeId; label: string }[] = [
  { value: 'batch', label: 'Batch' },
  { value: 'form', label: 'Form' },
  { value: 'field', label: 'Field' },
];

/**
 * Task 6 (E-062 / F-055) — registry-derived scope constraint. A scope is
 * offerable only when EVERY branch can execute at it: route branches execute
 * at exactly the moment-derived scope (`compileMultiActionRoute` ignores the
 * authored scope — offering others was the F-055 bug), check branches at
 * their effect's declared `validScopes`. Branches carrying unknown/legacy
 * effect keys impose no constraint (never falsely lock an old automation),
 * and the automation's CURRENT scope always stays selectable so legacy
 * content renders truthfully.
 */
function scopeDisabledReason(automation: Automation, scope: ScopeId): string | undefined {
  if (automation.scope === scope) return undefined;
  for (const branch of automation.branches) {
    if (branch.family === 'route') {
      const derived = MOMENT[automation.moment]?.scope;
      if (derived && derived !== scope) {
        return `Workflow actions at this moment act on the ${derived} — not a ${scope}.`;
      }
      continue;
    }
    const effect = effectByKey(branch.effectKey);
    if (effect && !effect.validScopes.includes(scope)) {
      return `"${effect.label}" acts on ${effect.validScopes.join(' or ')} — not ${scope}.`;
    }
  }
  return undefined;
}

function scopeSummary(automation: Automation, fields: ScopeField[]): string {
  if (automation.scope !== 'field') {
    return SCOPE_OPTIONS.find((o) => o.value === automation.scope)?.label ?? automation.scope;
  }
  const label = fields.find((f) => f.name === automation.fieldName)?.label ?? automation.fieldName;
  return `Field · ${label || 'not set'}`;
}

/** Batch/Form/Field radio; Field reveals a field picker (the scoped form's
 *  fields, per `RuleAutomationDrawer.tsx:180`'s `conditionFields`). */
export function ScopeSection({ automation, fields, onChange, ownerFormLabel }: Props) {
  function setScope(scope: ScopeId) {
    onChange({
      ...automation,
      scope,
      ...(scope !== 'field' ? { fieldName: undefined } : {}),
    });
  }

  return (
    <div data-testid="scope-section">
      <SectionCard title="Scope" summary={scopeSummary(automation, fields)} defaultOpen>
        <div className={styles.formGrid}>
          {ownerFormLabel && (
            <>
              <span className={styles.fieldLabel}>Form</span>
              <span
                className={styles.readOnlyValue}
                data-testid="scope-owner-form"
                title="This automation belongs to this form. To use a different form, delete it and create a new automation from the list."
              >
                {ownerFormLabel}
              </span>
            </>
          )}

          <span className={styles.fieldLabel} id="scope-applies-to-label">
            Applies to
          </span>
          {/* Builder follow-ups (item f) — role=radiogroup + aria-labelledby
           *  so screen readers announce this as one group named by the
           *  visible "Applies to" label, not three unrelated radios. */}
          <div className={styles.radioGroup} role="radiogroup" aria-labelledby="scope-applies-to-label">
            {SCOPE_OPTIONS.map((o) => {
              const disabledReason = scopeDisabledReason(automation, o.value);
              return (
                <label key={o.value} className={styles.checkboxLabel} title={disabledReason}>
                  <input
                    type="radio"
                    name="automation-scope"
                    checked={automation.scope === o.value}
                    disabled={disabledReason !== undefined}
                    onChange={() => setScope(o.value)}
                  />
                  {o.label}
                </label>
              );
            })}
          </div>

          {automation.scope === 'field' && (
            <>
              <label className={styles.fieldLabel} htmlFor="scope-field-select">
                Field
              </label>
              <select
                id="scope-field-select"
                className={styles.textInput}
                data-testid="scope-field-select"
                value={automation.fieldName ?? ''}
                onChange={(e) => onChange({ ...automation, fieldName: e.target.value })}
              >
                <option value="" disabled>
                  Select a field…
                </option>
                {fields.map((f) => (
                  <option key={f.name} value={f.name}>
                    {f.label || f.name}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
